/**
 * ConfirmModal — Diálogo de confirmación reutilizable.
 * Reemplaza confirm()/alert() nativos y las copias inline en cada página.
 *
 * Corre sobre el `Modal` del design system, y eso es lo que le da lo que antes
 * no tenía en sus **55 usos**: `role="dialog"` y `aria-modal` —sin eso, para un
 * lector de pantalla no era un diálogo sino contenido suelto, y nada anunciaba
 * que lo de atrás quedó inerte—, la trampa de foco, el bloqueo del scroll de
 * fondo, la restauración del foco al cerrar y el **portal**.
 *
 * 🔴 El portal importa acá más que en cualquier otro modal: una confirmación se
 * abre desde donde sea, y un ancestro con `backdrop-filter` la dejaría DEBAJO
 * del contenido sin que ningún `z-index` la rescate. Es el defecto que se
 * reportó en el panel de plataforma.
 *
 * Los dos botones son el `Button` del design system. La acción va con la
 * variante `*-solid` —relleno sólido y texto blanco, como las 55 confirmaciones
 * ya se veían— y no con `danger`, que es sutil (#332). 🔴 Antes salía de
 * `*-solid` (500 en claro, 400 en oscuro) y el texto blanco NO llegaba a AA en
 * ninguno de los seis casos: de 1,67:1 (warning en oscuro) a 3,76:1 (danger en
 * claro). Con `*-fill` los seis superan 4,5:1.
 *
 * ⚠️ El clic afuera **sigue cancelando**. El `Modal` ofrece
 * `dismissOnScrimClick={false}` justo para confirmaciones destructivas, pero
 * cancelar es el lado seguro: cerrar sin querer no destruye nada, y exigir el
 * botón sería un cambio de comportamiento que nadie pidió.
 */
import { Modal } from '@/components/ds/Modal'
import { Button } from '@/components/ds/Button'

export interface ConfirmModalProps {
  open: boolean
  message: string
  onConfirm: () => void
  onCancel: () => void
  isPending?: boolean
  variant?: 'danger' | 'success' | 'warning'
  confirmLabel?: string
  cancelLabel?: string
}

export function ConfirmModal({
  open,
  message,
  onConfirm,
  onCancel,
  isPending = false,
  variant = 'danger',
  confirmLabel = 'Confirmar',
  cancelLabel = 'Cancelar',
}: ConfirmModalProps) {
  if (!open) return null

  return (
    <Modal
      open
      onClose={onCancel}
      size="sm"
      // El mensaje ES el título del diálogo: sin `title` el `Modal` no puede
      // darle nombre accesible, y una confirmación sin nombre se anuncia como
      // «diálogo» a secas — justo donde hace falta saber qué se está por hacer.
      title={message}
      actions={
        <>
          <Button variant="secondary" onClick={onCancel}>{cancelLabel}</Button>
          <Button variant={`${variant}-solid`} onClick={onConfirm} loading={isPending}>
            {confirmLabel}
          </Button>
        </>
      }
    />
  )
}

/* ─── Hook para reemplazar confirm()/alert() nativos ────────────────────────── */

import { useState, useCallback } from 'react'

interface ConfirmState {
  message: string
  variant: 'danger' | 'success' | 'warning'
  confirmLabel?: string
  onConfirm: () => void
}

export function useConfirmModal() {
  const [state, setState] = useState<ConfirmState | null>(null)
  const [isPending, setIsPending] = useState(false)

  const confirm = useCallback((opts: {
    message: string
    variant?: 'danger' | 'success' | 'warning'
    confirmLabel?: string
    onConfirm: () => void | Promise<void>
  }) => {
    setState({
      message: opts.message,
      variant: opts.variant ?? 'danger',
      confirmLabel: opts.confirmLabel,
      onConfirm: async () => {
        setIsPending(true)
        try {
          await opts.onConfirm()
        } finally {
          setIsPending(false)
          setState(null)
        }
      },
    })
  }, [])

  const cancel = useCallback(() => setState(null), [])

  const modalProps: ConfirmModalProps = {
    open: state !== null,
    message: state?.message ?? '',
    variant: state?.variant ?? 'danger',
    confirmLabel: state?.confirmLabel,
    onConfirm: state?.onConfirm ?? (() => {}),
    onCancel: cancel,
    isPending,
  }

  return { confirm, cancel, modalProps }
}
